"use client";

import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
export default function IndustryInquiry() {
  const container = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useReducedMotion();
  const headline = ["Fale", "connosco", "."];
  const headline2 = ["Soluções", "de", "engenharia", "industrial."];
  useGSAP(() => {
    const el = container.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.to(".scale-bokeh", {
        yPercent: -6,
        ease: "none",
        scrollTrigger: {
          trigger: el,
          start: "top bottom",
          end: "bottom top",
          scrub: 1.2
        }
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: container
  });
  const wordVariants = {
    hidden: {
      opacity: 0,
      y: 18
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        delay: i * 0.06,
        ease: [0.22, 1, 0.36, 1] as const
      }
    })
  };
  const underlineVariants = {
    hidden: {
      scaleX: 0
    },
    visible: {
      scaleX: 1,
      transition: {
        duration: 0.5,
        delay: (headline.length + headline2.length) * 0.06 + 0.1,
        ease: [0.22, 1, 0.36, 1] as const
      }
    }
  };
  return <section data-section-id="26857" id="industryinquirycta" data-nav="dark" ref={container} className="relative overflow-hidden py-36 md:py-52 bg-primary text-primary-foreground">
      {}
      <div className="scale-bokeh absolute inset-0 bg-cover bg-center opacity-60 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/industrial_spray_bg.png)]" aria-hidden="true" />

      {}
      <div className="absolute inset-x-0 top-0 h-px bg-border" aria-hidden="true" />

      <div className="relative mx-auto max-w-[900px] px-6 md:px-8 text-center">
        {}
        <motion.p initial={{
        opacity: 0,
        y: 8
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.4
      }} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} className="mb-8 font-default text-xs font-medium uppercase tracking-widest text-secondary-foreground">
          <span className="inline-block mr-2 h-1.5 w-1.5 rounded-lg align-middle bg-secondary" aria-hidden="true" />
          Eurocontrol Portugal
        </motion.p>

        {}
        <div className="relative mb-12">
          <div className="absolute inset-0 -mx-8 -my-6 rounded-3xl bg-background/10 backdrop-blur-sm" aria-hidden="true" />
          <h1 className="relative font-default font-medium text-primary-foreground leading-[0.98]" style={{
          fontSize: "clamp(2.75rem, 6.2vw + 0.5rem, 6.5rem)",
          letterSpacing: "-0.035em"
        }}>
            <span className="block">
              {headline.map((word, i) => <motion.span key={`h1-${i}`} data-index={i} className="inline-block mr-[0.25em]" custom={i} variants={wordVariants} initial={prefersReducedMotion ? "visible" : "hidden"} whileInView="visible" viewport={{
              once: true,
              amount: 0.3
            }}>
                  {word}
                </motion.span>)}
            </span>
            <span className="block">
              {headline2.map((word, i) => {
              const idx = headline.length + i;
              const isAccent = word === "industrial.";
              return <motion.span key={`h2-${i}`} data-index={i} className="inline-block mr-[0.25em] relative" custom={idx} variants={wordVariants} initial={prefersReducedMotion ? "visible" : "hidden"} whileInView="visible" viewport={{
                once: true,
                amount: 0.3
              }}>
                    {isAccent ? <span className="relative inline-block">
                        industrial
                        <motion.span className="absolute left-0 right-[0.35em] bottom-[0.08em] block origin-left bg-secondary" style={{
                    height: "0.08em"
                  }} variants={underlineVariants} initial={prefersReducedMotion ? "visible" : "hidden"} whileInView="visible" viewport={{
                    once: true,
                    amount: 0.3
                  }} aria-hidden="true" />
                        <span>.</span>
                      </span> : word}
                  </motion.span>;
            })}
            </span>
          </h1>
        </div>

        {}
        <motion.p initial={prefersReducedMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.4
      }} transition={{
        duration: 0.7,
        delay: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }} className="mb-12 font-default font-normal leading-[1.55] text-primary-foreground/90" style={{
        fontSize: "clamp(1.0625rem, 0.35vw + 1rem, 1.25rem)",
        letterSpacing: "-0.005em"
      }}>
          Especialistas em sistemas de pulverização desde 1937.
        </motion.p>

        {}
        <motion.div initial={prefersReducedMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.4
      }} transition={{
        duration: 0.8,
        delay: 0.4,
        ease: [0.22, 1, 0.36, 1]
      }} className="flex justify-center px-6 sm:px-0">
          <Button asChild className="group inline-flex h-auto items-center gap-3 rounded-lg px-9 py-4 text-[17px] font-medium shadow-lg transition-all duration-300 w-full sm:w-auto bg-secondary text-secondary-foreground">
            <Link to="/ContactsPage">
              <span>Consulte-nos</span>
              <span className="flex h-7 w-7 items-center justify-center rounded-lg transition-transform duration-300 group-hover:rotate-45 bg-primary-foreground text-secondary" aria-hidden="true">
                <ArrowUpRight className="h-4 w-4" />
              </span>
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>;
}